import { Box, Button, Checkbox, Dialog, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { capitalize, formatNumberAsPrice } from '@shared/common'
import { getInvoiceDates, getPrimaryServiceTypeName, isInvoiceCanceled } from '@shared/invoices'
import { i18WithParams as t } from '@shared/locales'
import { calculateInvoicePayFacTotals, getInvoiceDepositTxns, paymentType, totalInvoiceDeposit } from '@shared/payment'
import { ButtonActions } from 'button_actions/button_actions'
import { useAdminCancelInvoice } from 'service-api/src/hooks/use_admin_invoice'
import { useClientCancelInvoice } from 'service-api/src/hooks/use_client_invoice'
import { ClientInvoiceService } from 'service-api/src/services/client_invoice.service'
import { captureError } from 'service-api/src/shared/monitoring'
import { CustomInvoiceAdminDto, CustomOrderAdminDto } from 'service-api/src/types/api'
import { TextInputMoney } from '../../textinput_money/textinput_money'
import { getRefundRestrictions } from './helper'

type Props = {
  invoice: CustomInvoiceAdminDto
  order: CustomOrderAdminDto
  isAdmin?: boolean
  open: boolean
  onClose: () => void
  onCancelInvoice: () => void
  onPreCancelInvoice?: () => Promise<void>
}
export const CancelInvoiceModal = ({
  invoice,
  order,
  isAdmin,
  open,
  onClose,
  onCancelInvoice,
  onPreCancelInvoice,
}: Props) => {
  const { accountName = '' } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const primaryType = getPrimaryServiceTypeName(invoice)
  const [refundMode, setRefundMode] = useState(false)
  const [overrideRefundAmount, setOverrideRefundAmount] = useState('')
  const [alsoCancelGroomingAppointment, setAlsoCancelGroomingAppointment] = useState<boolean>(true)

  const { startDate: invoiceStartDate } = useMemo(() => getInvoiceDates(invoice), [invoice])
  // refundSettingToApply tells us what deposit refund rate to use at this particular time
  // maxCancelBy is the latest time the invoice can be canceled
  const { refundSettingToApply } = useMemo(
    () => getRefundRestrictions(order, invoiceStartDate),
    [order, invoiceStartDate]
  )

  const refundRate = parseFloat(refundSettingToApply?.rate || '0')
  const refundEligibleAmount = (invoice.netPaid || 0) * refundRate

  const { refetch: clientCancelInvoice } = useClientCancelInvoice(accountName, order.id!, invoice.id!)
  const { refetch: adminCancelInvoice } = useAdminCancelInvoice(
    accountName,
    order.id!,
    invoice.id!,
    overrideRefundAmount ? parseFloat(overrideRefundAmount) : undefined,
    invoice.locationServiceType?.serviceType?.name === 'grooming' ? 'CANCELED' : undefined
  )

  const depositTotal = useMemo(() => {
    return totalInvoiceDeposit(invoice.id, order.payments)
  }, [invoice.id, order.payments])

  const invoiceDepositTxns = useMemo(() => {
    return getInvoiceDepositTxns(order.payments).get(invoice?.id || '') || []
  }, [order.payments, invoice?.id])
  const payFacTypes = useMemo(() => {
    return calculateInvoicePayFacTotals(invoiceDepositTxns)
  }, [invoiceDepositTxns])

  const applicableGroomsInOrder = useMemo(() => {
    return order.invoices?.filter(
      (invoice) =>
        invoice.items?.[0].locationServiceType?.serviceType?.name === 'grooming' &&
        invoice.isInStay &&
        !isInvoiceCanceled(invoice)
    )
  }, [order.invoices])

  useEffect(() => {
    if (!open) {
      setRefundMode(false)
    }
  }, [open])

  useEffect(() => {
    setOverrideRefundAmount(refundMode ? refundEligibleAmount.toFixed(2) : '')
  }, [refundMode, refundEligibleAmount])

  const renderRefundMode = () => (
    <>
      <Typography variant="h4" mb={3}>
        {t('cancelInvoice.overrideRefund.title')}
      </Typography>
      <Stack direction="row" justifyContent="space-between">
        <Stack>
          <Typography variant="body1">{t('global.label.amountPaid')}</Typography>
          <Typography variant="h6">{formatNumberAsPrice(invoice.netPaid || 0)}</Typography>
        </Stack>
        <TextInputMoney
          autoFocus={true}
          id="refund-amount"
          label={t('cancelInvoice.refundAmount')}
          value={overrideRefundAmount}
          onChange={(e) => setOverrideRefundAmount(e.target.value)}
        />
      </Stack>
    </>
  )

  const handleSubmit = async () => {
    if (onPreCancelInvoice) {
      await onPreCancelInvoice()
    }
    if (accountName && order.id && invoice.id) {
      // cancel all grooms in applicableGroomsInOrder as well if the invoice we're canceling isnt a groom
      if (
        !isAdmin &&
        alsoCancelGroomingAppointment &&
        invoice.items?.[0].locationServiceType?.serviceType?.name !== 'grooming'
      ) {
        applicableGroomsInOrder?.map((associatedGroom): void => {
          if (order.id && associatedGroom.id) {
            ClientInvoiceService.cancelInvoice(accountName, order.id, associatedGroom.id).catch((err) => {
              const errorMsg = err?.response?.data?.error ?? t('global.label.unexpectedError')
              enqueueSnackbar(errorMsg, { variant: 'warning' })
            })
          }
        })
      }
      const cancelInvoice = isAdmin ? adminCancelInvoice : clientCancelInvoice
      await cancelInvoice()
        .then(({ data, error }) => {
          if (data) {
            onCancelInvoice()
            enqueueSnackbar(
              t(
                alsoCancelGroomingAppointment
                  ? 'cancelInvoice.enqueue.multi.success'
                  : 'cancelInvoice.enqueue.single.success',
                { primary: capitalize(primaryType) }
              ),
              {
                variant: 'info',
              }
            )
          } else {
            // @ts-expect-error
            const errorMsg = error?.response?.data?.error ?? t('global.label.unexpectedError')
            enqueueSnackbar(errorMsg, { variant: 'warning' })
          }
        })
        .catch((error: Error) => {
          enqueueSnackbar(t('global.label.unexpectedError'), {
            variant: 'error',
          })
          captureError(error as Error)
        })
        .finally(() => {
          onClose()
        })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="modal-cancel-invoice"
      aria-describedby="modal-cancel-invoice-for-order"
    >
      <Box sx={{ p: 2, justifyContent: 'center', alignItems: 'center' }}>
        {refundMode ? (
          renderRefundMode()
        ) : (
          <>
            <Typography variant="h5" mb={1} sx={{ textTransform: 'capitalize' }}>
              {t('cancelInvoice.confirmCancel')}
            </Typography>
            {depositTotal > 0 ? (
              <>
                <Typography variant="body1" mb={1}>
                  {t('cancelInvoice.content')}
                </Typography>
                <Stack direction="row" sx={{ height: '48px', border: '1px solid #ccc' }}>
                  {payFacTypes.map(([payFacType, amount], index) => (
                    <Stack
                      flex={1}
                      alignItems="center"
                      justifyContent="center"
                      key={payFacType}
                      sx={{
                        borderRight: index === payFacTypes.length - 1 ? 'none' : '1px solid #ccc',
                      }}
                    >
                      <Typography variant="body1bold">{formatNumberAsPrice(amount * refundRate)}</Typography>
                      <Typography variant="body2">
                        {t('cancelInvoice.content.receipt', {
                          target: paymentType(payFacType),
                        })}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            ) : (
              <Typography variant="body1" mb={1}>
                {invoice.locationServiceType?.serviceType?.name === 'grooming'
                  ? t('cancelInvoice.content.payAtFacilityGroom')
                  : t('cancelInvoice.content.payAtFacility')}
              </Typography>
            )}
            {!isAdmin &&
              applicableGroomsInOrder &&
              applicableGroomsInOrder?.length > 0 &&
              invoice.items?.[0].locationServiceType?.serviceType?.name !== 'grooming' && (
                <>
                  <Typography variant="body1" mt={1}>
                    {t('cancelInvoice.grooms.description', {
                      count: (applicableGroomsInOrder?.length || 0).toString(),
                    })}
                  </Typography>
                  <Stack direction={'row'} spacing={1} mt={1}>
                    <Checkbox
                      style={{ padding: 0 }}
                      checked={alsoCancelGroomingAppointment}
                      onClick={() => setAlsoCancelGroomingAppointment(!alsoCancelGroomingAppointment)}
                    />
                    <Typography variant="body1">{t('cancelInvoice.grooms.notice')}</Typography>
                  </Stack>
                </>
              )}
            {isAdmin && (
              <Box>
                <Button variant="text" onClick={() => setRefundMode(true)}>
                  {t('cancelInvoice.overrideRefund.button')}
                </Button>
              </Box>
            )}
          </>
        )}
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <ButtonActions
            actionEnabled={true}
            onActionLabel={depositTotal > 0 ? t('global.label.confirmRefund') : t('global.label.confirm')}
            onAction={handleSubmit}
            onCancel={async () => onClose()}
            onCancelLabel={t('global.label.cancel')}
            actionButtonProps={{
              variant: 'contained',
            }}
            cancelButtonProps={{
              variant: 'outlined',
            }}
          />
        </Stack>
      </Box>
    </Dialog>
  )
}
